import { AuthState, useIdentify } from './useIdentify';

/**
 * The three audiences the freemium experience (Exp 255) distinguishes:
 *
 *   - `prospect` — anonymous / pre-signup.
 *   - `guest`    — signed in but not yet checked out (`did_checkout === false`).
 *   - `customer` — signed in and checked out.
 */
export type FreemiumAudience = 'prospect' | 'guest' | 'customer';

export interface FreemiumAudienceResult {
  isLoading: boolean;
  /**
   * The resolved audience, or `undefined` while identify is still loading or
   * when it errored. Callers should treat `undefined` (once `isLoading` is
   * false) as "unknown — fail closed", not as any particular audience.
   */
  audience: FreemiumAudience | undefined;
}

export function getFreemiumFunnelCtaText({
  audience,
}: {
  audience: FreemiumAudience | undefined;
}): string {
  if (audience === 'guest') {
    return 'Get your first delivery';
  }

  return 'Get recipes picked for you';
}

/**
 * Resolves the freemium audience from the identify read.
 *
 * The guest/customer split mirrors `RequireCheckedOut`: only an explicit
 * `did_checkout === false` for an authenticated customer is a guest; anything
 * else authenticated counts as checked out. An errored identify read resolves
 * (`isLoading: false`) with an `undefined` audience so callers can redirect
 * rather than hang.
 *
 * Lives next to `useIdentify` (rather than in `@hungryroot/app-public`) so
 * lower-level packages like `@hungryroot/pho` can tag telemetry with the
 * audience without depending upward on the freemium UI package.
 */
export function useFreemiumAudience(): FreemiumAudienceResult {
  const { authState, customerData } = useIdentify();

  switch (authState) {
    case AuthState.isLoading:
      return { isLoading: true, audience: undefined };
    case AuthState.isAnonymous:
      return { isLoading: false, audience: 'prospect' };
    case AuthState.isAuthenticated:
      return {
        isLoading: false,
        audience: customerData.did_checkout === false ? 'guest' : 'customer',
      };
    default:
      // AuthState.isError — resolved, but unknown. Fail closed.
      return { isLoading: false, audience: undefined };
  }
}
